import type { BecomeAdminCopy, BecomeAdminFormValues } from './types';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const fallbackValue = (value: string) => value.trim() || '—';

const contractRows = (values: BecomeAdminFormValues, t: BecomeAdminCopy) => [
  [t.becomeAdminCompanyName, values.companyName],
  [t.becomeAdminRegistrationNumber, values.registrationNumber],
  [t.becomeAdminCompanyAddress, values.companyAddress],
  [t.becomeAdminSector, values.sector],
  [t.becomeAdminCompanyEmail, values.companyEmail],
  [t.becomeAdminCompanyPhone, values.companyPhone],
  [t.becomeAdminRepName, values.repName],
  [t.becomeAdminRepRole, values.repRole],
  [t.becomeAdminRepEmail, values.repEmail],
  [t.becomeAdminRepPhone, values.repPhone],
];

export const buildAdminContractPreview = (values: BecomeAdminFormValues, t: BecomeAdminCopy) =>
  contractRows(values, t)
    .filter(([, value]) => value.trim())
    .slice(0, 6)
    .map(([label, value]) => ({ label, value }));

export const buildAdminContractHtml = (values: BecomeAdminFormValues, t: BecomeAdminCopy) => {
  const rows = contractRows(values, t)
    .map(
      ([label, value]) => `
        <tr>
          <th>${escapeHtml(label)}</th>
          <td>${escapeHtml(fallbackValue(value))}</td>
        </tr>
      `,
    )
    .join('');

  const clauses = [
    t.becomeAdminContractClauseAccess,
    t.becomeAdminContractClauseResponsibility,
    t.becomeAdminContractClauseData,
    t.becomeAdminContractClausePayment,
  ];

  return `<!doctype html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(t.becomeAdminContractTitle)}</title>
  <style>
    :root {
      color: #111827;
      background: #f8fafc;
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }

    body {
      margin: 0;
      padding: 48px;
    }

    main {
      max-width: 820px;
      margin: 0 auto;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      padding: 48px;
    }

    .eyebrow {
      color: #b7791f;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: .24em;
      text-transform: uppercase;
    }

    h1 {
      margin: 10px 0 8px;
      font-size: 34px;
      line-height: 1.08;
      letter-spacing: -.04em;
    }

    .muted {
      color: #64748b;
      line-height: 1.65;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 34px 0;
      font-size: 14px;
    }

    th, td {
      border-bottom: 1px solid #e5e7eb;
      padding: 14px 0;
      text-align: left;
      vertical-align: top;
    }

    th {
      width: 38%;
      color: #64748b;
      font-size: 11px;
      letter-spacing: .16em;
      text-transform: uppercase;
    }

    ol {
      padding-left: 20px;
      line-height: 1.75;
    }

    .signature {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 32px;
      margin-top: 52px;
    }

    .line {
      border-top: 1px solid #111827;
      padding-top: 10px;
      color: #64748b;
      font-size: 12px;
      letter-spacing: .14em;
      text-transform: uppercase;
    }

    @media print {
      body { padding: 0; background: #fff; }
      main { border: 0; }
    }
  </style>
</head>
<body>
  <main>
    <p class="eyebrow">Umbaji Data Hub</p>
    <h1>${escapeHtml(t.becomeAdminContractTitle)}</h1>
    <p class="muted">${escapeHtml(t.becomeAdminContractIntro)}</p>

    <table>
      <tbody>${rows}</tbody>
    </table>

    <p class="eyebrow">${escapeHtml(t.becomeAdminContractTermsTitle)}</p>
    <ol>
      ${clauses.map((clause) => `<li>${escapeHtml(clause)}</li>`).join('')}
    </ol>

    <section class="signature">
      <div class="line">${escapeHtml(t.becomeAdminContractSignature)}</div>
      <div class="line">${escapeHtml(t.becomeAdminContractDate)}</div>
    </section>
  </main>
</body>
</html>`;
};

const replaceTemplatePlaceholders = (templateHtml: string, values: BecomeAdminFormValues) => {
  const replacements: Record<string, string> = {
    company_name: values.companyName,
    registration_number: values.registrationNumber,
    sector: values.sector,
    company_address: values.companyAddress,
    company_email: values.companyEmail,
    company_phone: values.companyPhone,
    company_website: values.companyWebsite,
    representative_name: values.repName,
    representative_role: values.repRole,
    representative_email: values.repEmail,
    representative_phone: values.repPhone,
    name: values.repName,
    email: values.repEmail,
  };

  return templateHtml.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_, key: string) =>
    escapeHtml(fallbackValue(replacements[key.toLowerCase()] ?? '')),
  );
};

const wrapContractFragment = (contentHtml: string, t: BecomeAdminCopy) => `<!doctype html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(t.becomeAdminContractTitle)}</title>
  <style>
    body { margin: 0; padding: 48px; color: #111827; background: #f8fafc; font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
    main { max-width: 820px; margin: 0 auto; background: #fff; border: 1px solid #e5e7eb; padding: 48px; }
    table { width: 100%; border-collapse: collapse; margin: 28px 0; }
    th, td { border-bottom: 1px solid #e5e7eb; padding: 12px 0; text-align: left; vertical-align: top; }
    th { width: 38%; color: #64748b; font-size: 11px; letter-spacing: .16em; text-transform: uppercase; }
    ol { padding-left: 20px; line-height: 1.75; }
    @media print { body { padding: 0; background: #fff; } main { border: 0; } }
  </style>
</head>
<body><main>${contentHtml}</main></body>
</html>`;

export const buildAdminContractHref = (
  values: BecomeAdminFormValues,
  t: BecomeAdminCopy,
  templateHtml?: string | null,
) => {
  const renderedHtml = templateHtml?.trim()
    ? wrapContractFragment(replaceTemplatePlaceholders(templateHtml, values), t)
    : buildAdminContractHtml(values, t);

  return `data:text/html;charset=utf-8,${encodeURIComponent(renderedHtml)}`;
};

